import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  useTheme,
  alpha,
  Tooltip,
  Avatar,
  Snackbar,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  CameraAlt,
  CheckCircle,
  Delete,
  Info,
  AccessTime,
  DirectionsCar,
  Description,
  LocationOn,
  Save,
  ExpandMore,
  Close
} from '@mui/icons-material';
import { caseService } from '../services/api';
import { carIcons } from '../data/sampleImages';
import { fitImageWithin } from '../utils/imageProcessor';
import { hasCoordinates } from '../utils/location';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
// Import company logo
import logo from '../assets/logo.svg';

// List of required photos for vehicle inspection
const getRequiredPhotos = (t) => [
  { id: 'front_windscreen', label: t('inspection.photoLabels.front_windscreen'), description: t('inspection.photoDescriptions.front_windscreen') },
  { id: 'front_left_corner', label: t('inspection.photoLabels.front_left_corner'), description: t('inspection.photoDescriptions.front_left_corner') },
  { id: 'rear_left_corner', label: t('inspection.photoLabels.rear_left_corner'), description: t('inspection.photoDescriptions.rear_left_corner') },
  { id: 'rear_right_corner', label: t('inspection.photoLabels.rear_right_corner'), description: t('inspection.photoDescriptions.rear_right_corner') },
  { id: 'front_right_corner', label: t('inspection.photoLabels.front_right_corner'), description: t('inspection.photoDescriptions.front_right_corner') },
  { id: 'odometer', label: t('inspection.photoLabels.odometer'), description: t('inspection.photoDescriptions.odometer') },
  { id: 'chassis_number', label: t('inspection.photoLabels.chassis_number'), description: t('inspection.photoDescriptions.chassis_number') }
];

// Format date function
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const KeyValueGrid = ({ items }) => (
  <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 0, m: 0 }}>
    {items.map(({ label, value }) => (
      <Box key={label} component="div" sx={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 42%) 1fr', gap: 1.5, px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider', minWidth: 0 }}>
        <Typography component="dt" variant="body2" color="text.secondary">{label}</Typography>
        <Typography component="dd" variant="body2" fontWeight={600} sx={{ m: 0, overflowWrap: 'anywhere' }}>{value || 'N/A'}</Typography>
      </Box>
    ))}
  </Box>
);

// Time remaining function has been removed as per requirements

const InspectionPage = () => {
  const { referenceNumber } = useParams();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const RequiredPhotos = useMemo(() => getRequiredPhotos(t), [t]);
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openInstructions, setOpenInstructions] = useState(true);
  const [capturedImages, setCapturedImages] = useState({});
  const [currentPhoto, setCurrentPhoto] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [stream, setStream] = useState(null);

  // New state variables for enhanced functionality
  const [location, setLocation] = useState({ latitude: null, longitude: null, locationName: 'Fetching location...' });
  const [locationStatus, setLocationStatus] = useState('requesting');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [capturedImageData, setCapturedImageData] = useState(null);
  const [syncingImage, setSyncingImage] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [referenceId, setReferenceId] = useState('');

  const requestLocation = useCallback(() => new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      setLocationStatus('unsupported');
      setLocation(prev => ({ ...prev, locationName: 'Location is not supported by this browser' }));
      reject(new Error('Geolocation is not supported'));
      return;
    }

    setLocationStatus('requesting');
    setLocation(prev => ({ ...prev, locationName: 'Requesting location permission...' }));
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const fallbackName = `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
        setLocationStatus('granted');
        setLocation({ latitude, longitude, locationName: fallbackName });
        resolve({ latitude, longitude });

        fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`)
          .then(response => response.json())
          .then(data => {
            const locationName = data.display_name
              ? data.display_name.split(',').slice(0, 2).join(', ')
              : fallbackName;
            setLocation({ latitude, longitude, locationName });
          })
          .catch(() => {});
      },
      (locationError) => {
        const denied = locationError.code === 1;
        setLocationStatus(denied ? 'denied' : 'unavailable');
        setLocation(prev => ({
          ...prev,
          latitude: null,
          longitude: null,
          locationName: denied
            ? 'Location blocked. Allow it in browser settings, then retry.'
            : 'Could not get location. Move outdoors and retry.'
        }));
        reject(locationError);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  }), []);

  // Fetch case data and get location
  useEffect(() => {
    const fetchCaseData = async () => {
      try {
        setLoading(true);
        console.log('Fetching case data for:', referenceNumber);

        // Use the API service to get real case data
        const response = await caseService.getCaseByRefNo(referenceNumber);
        console.log('Case data fetched:', response);

        // Extract the case data from the nested response structure
        const caseData = response?.data?.case;
        console.log('Extracted case data:', caseData);

        if (caseData) {
          // Check case status immediately and redirect if needed
          if (caseData.status === 'Reassigned') {
            console.log('Case is reassigned, redirecting to reassignment page');
            // Directly change the URL to ensure it works in all environments
            window.location.replace(`/reassignment/${referenceNumber}`);
            return;
          } else if (caseData.status === 'Processed') {
            console.log('Case is already processed, should redirect to review page');
            // We don't redirect immediately to allow the user to see the data first
          }

          setCaseData(caseData);

          // Load only photos that have a confirmed cloud URL.
          loadImagesFromStorage(caseData.web_reference_number || referenceNumber);
        } else {
          setError('Failed to load case data');
        }
      } catch (error) {
        console.error('Error fetching case data:', error);
        setError('Failed to load case data. The reference link may be invalid or expired.');
      } finally {
        setLoading(false);
      }
    };

    if (referenceNumber) {
      fetchCaseData();
      requestLocation().catch(() => {});
    } else {
      setError(t('inspection.error.invalidReference'));
      setLoading(false);
    }
  }, [referenceNumber, i18n.language, requestLocation]);

  // Fetch images from server when component loads or refreshes
  useEffect(() => {
    if (!caseData?.web_reference_number) return;

    const fetchImagesFromServer = async () => {
      try {
        console.log('Fetching images from server for:', caseData.web_reference_number);

        // Use the API service to fetch images
        const response = await caseService.getCaseImages(caseData.web_reference_number);

        if (response && response.status === 'success' && response.data && response.data.length > 0) {

          // Check if there are reassigned images, if so redirect to reassignment page
          if (response.hasReassignedImages && response.caseStatus === 'Reassigned') {
            console.log('Case has reassigned images, redirecting to reassignment page');
            window.location.href = `/reassignment/${caseData.web_reference_number}`;
            return;
          }

          // Use functional state update to always work with the latest state
          setCapturedImages(prevCapturedImages => {
            const newCapturedImages = { ...prevCapturedImages };
            let imagesUpdated = false;

            // Process each image from the server
            response.data.forEach(img => {
              if (img.photoType) {
                // Check if we already have this image and should update it
                const shouldUpdate = !newCapturedImages[img.photoType] ||
                  // Always update if we have an Azure URL (prefer Azure storage)
                  (img.azureUrl && !newCapturedImages[img.photoType].includes('blob.core.windows.net'));

                if (shouldUpdate) {
                  if (img.azureUrl) {
                    newCapturedImages[img.photoType] = img.azureUrl;
                    imagesUpdated = true;
                  }
                }
              }
            });

            // Also check localStorage for any Azure URLs that might be cached
            // We use the RequiredPhotos from the closure, which is fine as it's memoized
            RequiredPhotos.forEach(photo => {
              const azureUrl = localStorage.getItem(`azure_image_${caseData.web_reference_number}_${photo.id}`);
              if (azureUrl && (!newCapturedImages[photo.id] || !newCapturedImages[photo.id].includes('blob.core.windows.net'))) {
                newCapturedImages[photo.id] = azureUrl;
                imagesUpdated = true;
              }
            });

            // Only return new state if something actually changed
            if (imagesUpdated) {
              console.log('Updating captured images state');
              return newCapturedImages;
            }
            return prevCapturedImages;
          });

          // Check if the case status is Processed
          if (response.caseStatus === 'Processed') {
            // Update case data with the processed status
            setCaseData(prev => ({ ...prev, status: 'Processed' }));
          }

        } else {
          console.log('No images found on server or error in response');
        }
      } catch (error) {
        console.error('Error fetching images from server:', error);
      }
    };

    fetchImagesFromServer();
    // Intentionally omitting RequiredPhotos from dependency array to avoid re-fetching on language change
    // The functional update inside handles the state merging correctly
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseData?.web_reference_number]);

  // Load images from localStorage
  const loadImagesFromStorage = (referenceNumber) => {
    try {
      const savedImages = {};
      RequiredPhotos.forEach(photo => {
        const cloudImage = localStorage.getItem(`azure_image_${referenceNumber}_${photo.id}`);
        if (cloudImage) {
          savedImages[photo.id] = cloudImage;
        }
      });
      setCapturedImages(savedImages);
    } catch (err) {
      console.error('Error loading images from storage:', err);
    }
  };

  // Save image to localStorage
  const saveImageToStorage = (photoId, imageData) => {
    try {
      localStorage.setItem(`inspection_${caseData.web_reference_number}_${photoId}`, imageData);
    } catch (err) {
      console.error('Error saving image to storage:', err);
    }
  };

  // Delete image from localStorage
  const deleteImageFromStorage = (photoId) => {
    try {
      localStorage.removeItem(`inspection_${caseData.web_reference_number}_${photoId}`);
    } catch (err) {
      console.error('Error deleting image from storage:', err);
    }
  };

  // Handle camera open with comprehensive error handling and permissions check
  const handleOpenCamera = async (photoId) => {
    try {
      await requestLocation();
    } catch (locationError) {
      setSnackbar({
        open: true,
        message: locationError.code === 1
          ? 'Location permission is required. Allow location in your browser settings, then retry.'
          : 'A valid location is required before taking photos. Please retry.',
        severity: 'warning'
      });
      return;
    }

    setCurrentPhoto(photoId);
    setCameraReady(false);
    setCameraOpen(true);

    console.log('Attempting to access camera...');

    // Check if we're in a secure context (HTTPS or localhost)
    if (!window.isSecureContext) {
      console.error('Not in a secure context. Camera access requires HTTPS.');
      alert('Camera access requires a secure connection (HTTPS). Please ensure you are accessing this application via HTTPS.');
      handleCloseCamera();
      return;
    }

    // Polyfill for older browsers
    if (!navigator.mediaDevices) {
      navigator.mediaDevices = {};
    }

    // Polyfill getUserMedia
    if (!navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia = function (constraints) {
        const getUserMedia = navigator.webkitGetUserMedia || navigator.mozGetUserMedia || navigator.msGetUserMedia;

        if (!getUserMedia) {
          console.error('MediaDevices API not supported in this browser');
          alert('Camera is not supported in your browser. Please try using a different browser like Chrome or Firefox.');
          handleCloseCamera();
          return Promise.reject(new Error('getUserMedia is not implemented in this browser'));
        }

        return new Promise(function (resolve, reject) {
          getUserMedia.call(navigator, constraints, resolve, reject);
        });
      };
    }

    // Check for permissions first
    try {
      // Access device camera with comprehensive error handling
      const constraints = {
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      };

      console.log('Requesting camera with constraints:', constraints);

      navigator.mediaDevices.getUserMedia(constraints)
        .then(mediaStream => {
          console.log('Camera access granted successfully');
          setStream(mediaStream);
          if (videoRef.current) {
            videoRef.current.srcObject = mediaStream;
            videoRef.current.onloadedmetadata = () => {
              console.log('Video metadata loaded, playing video');
              videoRef.current.play()
                .then(() => setCameraReady(true))
                .catch(e => console.error('Error playing video:', e));
            };
          }
        })
        .catch(err => {
          console.error('Error accessing camera:', err.name, err.message);
          let errorMessage = 'Unable to access camera. ';

          if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
            errorMessage += 'You denied camera permission. Please allow camera access in your browser settings and try again.';
          } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
            errorMessage += 'No camera found on your device.';
          } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
            errorMessage += 'Your camera is already in use by another application.';
          } else if (err.name === 'OverconstrainedError') {
            errorMessage += 'The requested camera constraints cannot be satisfied.';
          } else {
            errorMessage += 'Please ensure you have granted camera permissions and are using a supported browser.';
          }

          alert(errorMessage);
          handleCloseCamera();
        });
    } catch (error) {
      console.error('Exception when accessing camera:', error);
      alert('An error occurred while trying to access the camera. Please try again or use a different device.');
      handleCloseCamera();
    }
  };

  // Handle camera close
  const handleCloseCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => {
        console.log('Stopping track:', track.kind, track.label);
        track.stop();
      });
      setStream(null);
    }
    setCameraReady(false);
    setCameraOpen(false);
    setCurrentPhoto(null);
  };

  const pauseCameraForReview = () => {
    const activeStream = videoRef.current?.srcObject || stream;
    activeStream?.getTracks().forEach(track => track.stop());
    if (videoRef.current) videoRef.current.srcObject = null;
    setStream(null);
    setCameraReady(false);
    setCameraOpen(false);
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    if (e.target.files && e.target.files[0] && currentPhoto) {
      const file = e.target.files[0];

      // Check if the file is an image
      if (!file.type.match('image.*')) {
        alert('Please select an image file');
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const imageData = event.target.result;
        // Save the captured image
        const newCapturedImages = { ...capturedImages, [currentPhoto]: imageData };
        setCapturedImages(newCapturedImages);
        saveImageToStorage(currentPhoto, imageData);
        handleCloseCamera();
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle capture photo
  const handleCapturePhoto = () => {
    try {
      if (videoRef.current && canvasRef.current && currentPhoto) {
        const video = videoRef.current;
        const canvas = canvasRef.current;

        // Keep enough detail for inspection while avoiding multi-megabyte mobile photos.
        const dimensions = fitImageWithin(video.videoWidth, video.videoHeight);
        canvas.width = dimensions.width;
        canvas.height = dimensions.height;

        // Draw video frame to canvas
        const context = canvas.getContext('2d');
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        const aiImageData = canvas.toDataURL('image/jpeg', 0.78);

        // Add timestamp and location stamp to the image
        const now = new Date();
        const timestamp = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;

        // Safely handle location data
        const lat = (location && typeof location.latitude === 'number') ? location.latitude.toFixed(6) : '0.000000';
        const lng = (location && typeof location.longitude === 'number') ? location.longitude.toFixed(6) : '0.000000';
        const locationText = `${lat}, ${lng}`;

        // Style for the stamps
        context.fillStyle = 'rgba(0, 0, 0, 0.5)';
        context.fillRect(0, canvas.height - 60, canvas.width, 60);

        context.fillStyle = 'white';
        context.font = '14px Arial';
        context.fillText(`Date: ${timestamp}`, 10, canvas.height - 40);
        context.fillText(`Location: ${locationText}`, 10, canvas.height - 15);

        // Convert canvas to image data URL
        const imageData = canvas.toDataURL('image/jpeg', 0.78);

        // Store captured image data temporarily
        setCapturedImageData({
          id: currentPhoto,
          data: imageData,
          aiData: aiImageData,
          latitude: (location && typeof location.latitude === 'number') ? location.latitude : null,
          longitude: (location && typeof location.longitude === 'number') ? location.longitude : null,
          timestamp: timestamp
        });

        // Show confirmation dialog
        setShowConfirmDialog(true);
        pauseCameraForReview();
      }
    } catch (error) {
      console.error('Error in handleCapturePhoto:', error);
      alert('Failed to capture photo. Please try again.');
    }
  };

  // Handle save image after confirmation
  const handleSaveImage = async (sync = false) => {
    if (!capturedImageData) return;

    const { id, data, aiData, latitude, longitude } = capturedImageData;

    try {
      if (sync) {
        await syncImageWithDatabase(id, data, aiData, latitude, longitude);
      }
    } catch {
      return;
    }

    // Mark the photo complete only after cloud storage confirms the upload.
    setCapturedImages(prev => ({ ...prev, [id]: data }));
    saveImageToStorage(id, data);

    // Close dialogs
    setShowConfirmDialog(false);
    handleCloseCamera();
    setCapturedImageData(null);
  };

  // Calculate completion percentage based on required photos
  const calculateCompletionPercentage = () => {
    if (!capturedImages) return 0;
    const capturedCount = Object.keys(capturedImages).length;
    return Math.round((capturedCount / RequiredPhotos.length) * 100);
  };

  // Check if all required photos have been captured
  const allPhotosCompleted = () => {
    return RequiredPhotos.every(photo => capturedImages[photo.id]);
  };

  // Get submission remarks if available
  const getSubmissionRemarks = () => {
    return caseData?.submission_remarks || 'No remarks provided';
  };

  // Submit the inspection (all photos)
  const handleSubmitInspection = async () => {
    if (!allPhotosCompleted()) return;

    try {
      setSubmitting(true);
      console.log('Submitting inspection for:', caseData.web_reference_number);

      // Call API to submit the inspection using web reference number instead of ID
      const result = await caseService.updateCaseStatus(caseData.web_reference_number, { status: 'Processed', updatedBy: 'Customer' });
      console.log('Submission result:', result);

      if (result.status === 'success') {
        // Update case data with the processed status
        setCaseData(prev => ({ ...prev, status: 'Processed' }));
        setReferenceId(caseData.web_reference_number);
        setShowSuccessDialog(true);
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to submit inspection. Please try again.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error submitting inspection:', error);
      setSnackbar({
        open: true,
        message: `Failed to submit inspection: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Sync image with database
  const syncImageWithDatabase = async (photoId, imageData, aiImageData, latitude, longitude) => {
    try {
      setSyncingImage(true);
      console.log('Starting image sync process for:', photoId);
      console.log('Reference number:', caseData.web_reference_number);
      console.log('Location data:', { latitude, longitude });

      // Prepare image data for API
      const imageInfo = {
        id: photoId,
        data: imageData,
        aiData: aiImageData,
        latitude,
        longitude,
        timestamp: new Date().toISOString()
      };

      console.log('Image info prepared, calling API...');

      // Call API to sync image
      const result = await caseService.syncImage(caseData.web_reference_number, imageInfo);
      console.log('API sync result:', result);

      setSnackbar({
        open: true,
        message: 'Photo uploaded and saved.',
        severity: 'success'
      });

      // Note: We no longer automatically update status to Processed when all photos are completed
      // User must explicitly click the Submit button

    } catch (error) {
      console.error('Error syncing image:', error);
      console.error('Error details:', error.response ? error.response.data : 'No response data');
      console.error('Error status:', error.response ? error.response.status : 'No status');

      setSnackbar({
        open: true,
        message: 'Photo was not uploaded. Check your connection and tap Use photo to retry.',
        severity: 'error'
      });
      throw error;
    } finally {
      console.log('Sync process completed');
      setSyncingImage(false);
    }
  };

  // Handle recapture photo
  const handleRecapturePhoto = () => {
    const photoId = currentPhoto;
    setShowConfirmDialog(false);
    setCapturedImageData(null);
    if (photoId) handleOpenCamera(photoId);
  };

  // Handle delete photo
  const handleDeletePhoto = (photoId) => {
    // Remove from state
    setCapturedImages(prev => {
      const newImages = { ...prev };
      delete newImages[photoId];
      return newImages;
    });

    // Remove from localStorage
    deleteImageFromStorage(photoId);
  };

  // Handle close instructions dialog
  const handleCloseInstructions = () => {
    setOpenInstructions(false);
  };

  // Handle snackbar close
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          Loading inspection details...
        </Typography>
      </Box>
    );
  }

  // If the case is processed and we're not showing the success dialog, redirect to review page
  if (caseData && caseData.status === 'Processed' && !showSuccessDialog) {
    return (
      <Box sx={{
        p: 4,
        textAlign: 'center',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            backgroundColor: alpha(theme.palette.info.main, 0.1),
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            maxWidth: 500
          }}
        >
          <Info color="info" sx={{ fontSize: 48, mb: 2, opacity: 0.7 }} />
          <Typography variant="h5" color="primary" gutterBottom>
            Inspection Under Review
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Thank you for submitting your inspection. Our team is currently reviewing your case and will process it shortly.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Reference Number: <strong>{caseData.web_reference_number}</strong>
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{
        p: 4,
        textAlign: 'center',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            backgroundColor: alpha(theme.palette.error.main, 0.1),
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            maxWidth: 500
          }}
        >
          <Info color="error" sx={{ fontSize: 48, mb: 2, opacity: 0.7 }} />
          <Typography variant="h5" color="error" gutterBottom>
            Error Loading Inspection
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {error}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => window.location.reload()}
          >
            Try Again
          </Button>
        </Paper>
      </Box>
    );
  }

  // Calculate completion percentage
  const completedPhotos = Object.keys(capturedImages).length;
  const totalPhotos = RequiredPhotos.length;
  const currentPhotoDetails = RequiredPhotos.find(photo => photo.id === currentPhoto);
  const currentPhotoNumber = RequiredPhotos.findIndex(photo => photo.id === currentPhoto) + 1;
  const detailSections = [
    {
      title: t('caseDetails.policyInfo'),
      icon: <Description fontSize="small" />,
      items: [
        { label: t('caseDetails.referenceNumber'), value: caseData.web_reference_number },
        { label: 'Quote Number', value: caseData.quote_number },
        { label: t('caseDetails.startDate'), value: formatDate(caseData.policy_start_date) },
        { label: t('caseDetails.endDate'), value: formatDate(caseData.policy_expiry_date) },
        { label: t('caseDetails.policyCoverType'), value: caseData.policy_cover_type },
        { label: 'Cover Details', value: caseData.cover_details },
        { label: 'Previous Policy Expiry', value: formatDate(caseData.previous_policy_expiry_date) },
        { label: 'Previous Policy Cover', value: caseData.previous_policy_cover_type },
      ],
    },
    {
      title: t('caseDetails.vehicleInfo'),
      icon: <DirectionsCar fontSize="small" />,
      items: [
        { label: 'Plate Character', value: caseData.vehicle_plate_character },
        { label: t('caseDetails.plateNumber'), value: caseData.vehicle_plate },
        { label: t('caseDetails.engine'), value: caseData.engine_number },
        { label: t('caseDetails.chassisNumber'), value: caseData.chassis_number },
        { label: t('caseDetails.make'), value: caseData.make },
        { label: t('caseDetails.model'), value: caseData.model },
        { label: 'Body Type', value: caseData.body_type },
        { label: 'Manufacturing Year', value: caseData.manufacturing_year },
        { label: t('caseDetails.reasonForInspection'), value: caseData.reason_for_inspection },
      ],
    },
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: 'background.default',
      maxWidth: 1120,
      mx: 'auto',
      p: { xs: 1, sm: 1.5, md: 2 },
      overflowX: 'hidden'
    }}>
      {/* Company header */}
      <Paper sx={{
        px: { xs: 1.25, sm: 1.75 },
        py: 1,
        mb: 1.5,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper'
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <img
            src={logo}
            alt="Company Logo"
            style={{
              height: '26px',
              width: 'auto'
            }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              component="h1"
              variant="h6"
              sx={{
                display: { xs: 'none', sm: 'block' },
                fontSize: { xs: '0.95rem', sm: '1.05rem' },
                whiteSpace: 'nowrap'
              }}
            >
              {t('app.title')}
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto', flexShrink: 0 }}>
            <LanguageSwitcher compact />
          </Box>
        </Box>
      </Paper>
      {/* Instructions Dialog */}
      <Dialog
        open={openInstructions}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 1.5, margin: { xs: 1, sm: 2 }, width: { xs: 'calc(100% - 16px)', sm: 480 } }
        }}
      >
        <DialogTitle sx={{
          borderBottom: 1,
          borderColor: 'divider',
          p: 2.5
        }}>
          <Box display="flex" alignItems="center">
            <Info color="primary" fontSize="small" sx={{ mr: 1 }} />
            <Typography variant="h4">
              Before you start
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2.5 }}>
          <Box
            component="ul"
            sx={{
              listStyle: 'none',
              p: 0,
              my: 0,
              display: 'grid',
              gap: 1.5,
              '& li': { display: 'flex', gap: 1.25, alignItems: 'flex-start' },
            }}
          >
            {[
              'Review the case and vehicle details before you begin.',
              'Take clear, well-lit photos. Blurry images may delay approval.',
              'Check the customer and vehicle information to avoid errors or delays.',
              'Allow camera and location access, then capture each photo live.',
            ].map(instruction => (
              <Box component="li" key={instruction}>
                <CheckCircle color="success" sx={{ fontSize: 20, mt: 0.1, flexShrink: 0 }} />
                <Typography variant="body2">{instruction}</Typography>
              </Box>
            ))}
          </Box>

          <Paper variant="outlined" sx={{ mt: 2.5, p: 1.75, bgcolor: 'background.default' }}>
            <Typography variant="caption" color="text.secondary">Need help? Customer support</Typography>
            <Typography
              component="a"
              href="tel:08046512500"
              variant="body1"
              fontWeight={700}
              color="primary.main"
              sx={{ display: 'block', mt: 0.25, textDecoration: 'none' }}
            >
              080 4651 2500
            </Typography>
          </Paper>

          <Alert severity="warning" icon={false} sx={{ mt: 2, py: 0.5 }}>
            <Typography variant="body2" fontWeight={600}>
              Complete and submit all required photos before the inspection link expires.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button
            variant="contained"
            onClick={handleCloseInstructions}
            fullWidth
          >
            I understand — start inspection
          </Button>
        </DialogActions>
      </Dialog>

      {/* Full-screen camera */}
      <Dialog
        open={cameraOpen}
        fullScreen
        PaperProps={{
          sx: {
            borderRadius: 0,
            overflow: 'hidden',
            bgcolor: 'black',
            height: '100vh',
            '@supports (height: 100dvh)': { height: '100dvh' }
          }
        }}
      >
        <DialogContent sx={{ p: '0 !important', position: 'relative', bgcolor: 'black', overflow: 'hidden' }}>
          <video
            ref={videoRef}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              background: '#000'
            }}
            autoPlay
            playsInline
            muted
          />

          {!cameraReady && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', bgcolor: 'black' }}>
              <Box sx={{ textAlign: 'center', color: 'white' }}>
                <CircularProgress color="inherit" size={34} />
                <Typography variant="body2" sx={{ mt: 1.5 }}>Starting camera…</Typography>
              </Box>
            </Box>
          )}

          {/* The guide stays over the live image without cropping the preview. */}
          <Box sx={{
            position: 'absolute',
            left: '8%',
            right: '8%',
            top: { xs: '25%', sm: '22%' },
            bottom: { xs: '25%', sm: '23%' },
            border: '2px solid rgba(255,255,255,0.9)',
            borderRadius: 2,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.08)',
            pointerEvents: 'none'
          }} />

          {/* Photo guidance and close control are placed directly on the camera. */}
          <Box sx={{
            position: 'absolute',
            inset: '0 0 auto 0',
            zIndex: 2,
            color: 'white',
            px: 2,
            pt: 'max(14px, env(safe-area-inset-top))',
            pb: 5,
            background: 'linear-gradient(180deg, rgba(0,0,0,.88) 0%, rgba(0,0,0,.55) 65%, transparent 100%)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.75)', fontWeight: 700 }}>
                  PHOTO {currentPhotoNumber || 1} OF {totalPhotos}
                </Typography>
                <Typography variant="h6" fontWeight={700} lineHeight={1.25}>
                  {currentPhotoDetails?.label}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, color: 'rgba(255,255,255,.88)', maxWidth: 520 }}>
                  {currentPhotoDetails?.description}
                </Typography>
              </Box>
              <IconButton
                onClick={handleCloseCamera}
                aria-label="Close camera"
                sx={{ color: 'white', bgcolor: 'rgba(0,0,0,.45)', '&:hover': { bgcolor: 'rgba(0,0,0,.65)' } }}
              >
                <Close />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{
            position: 'absolute',
            top: { xs: 150, sm: 130 },
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 3,
            display: 'flex',
            alignItems: 'center',
            bgcolor: 'rgba(0,0,0,.62)',
            color: 'white',
            borderRadius: 10,
            px: 1.25,
            py: 0.6,
            maxWidth: 'calc(100% - 32px)'
          }}>
            <LocationOn fontSize="small" sx={{ mr: 0.5 }} />
            <Typography variant="caption" noWrap>
              {location.locationName}
            </Typography>
          </Box>

          {/* Capture control remains visible over the camera in every orientation. */}
          <Box sx={{
            position: 'absolute',
            inset: 'auto 0 0 0',
            zIndex: 3,
            display: 'grid',
            justifyItems: 'center',
            color: 'white',
            pt: 6,
            pb: 'max(18px, env(safe-area-inset-bottom))',
            background: 'linear-gradient(0deg, rgba(0,0,0,.9) 0%, rgba(0,0,0,.56) 62%, transparent 100%)'
          }}>
            <IconButton
              onClick={handleCapturePhoto}
              disabled={!cameraReady}
              aria-label={`Capture ${currentPhotoDetails?.label || 'photo'}`}
              sx={{
                width: 76,
                height: 76,
                bgcolor: 'white',
                color: 'primary.main',
                border: '5px solid rgba(255,255,255,.38)',
                backgroundClip: 'padding-box',
                boxShadow: '0 4px 18px rgba(0,0,0,.35)',
                '&:hover': { bgcolor: 'grey.100' },
                '&.Mui-disabled': { bgcolor: 'grey.500', color: 'grey.700' }
              }}
            >
              <CameraAlt sx={{ fontSize: 32 }} />
            </IconButton>
            <Typography variant="caption" fontWeight={700} sx={{ mt: 0.75 }}>
              Take photo
            </Typography>
          </Box>
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </DialogContent>
      </Dialog>

      {/* Full-screen review keeps the whole captured frame visible before upload. */}
      <Dialog
        open={showConfirmDialog}
        onClose={syncingImage ? undefined : handleRecapturePhoto}
        fullScreen
        PaperProps={{ sx: { bgcolor: 'black', borderRadius: 0, height: '100dvh', display: 'flex', flexDirection: 'column' } }}
      >
        <DialogTitle sx={{ px: 2, py: 1.25, bgcolor: 'black', color: 'white', borderBottom: '1px solid rgba(255,255,255,.14)' }}>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,.7)' }}>Review photo</Typography>
          <Typography variant="h6" fontWeight={700}>{currentPhotoDetails?.label}</Typography>
        </DialogTitle>
        <DialogContent sx={{ p: '0 !important', bgcolor: 'black', position: 'relative', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {capturedImageData && (
            <img
              src={capturedImageData.data}
              alt={`Captured ${currentPhotoDetails?.label || 'inspection photo'}`}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          )}
          {syncingImage && (
            <Box sx={{ position: 'absolute', inset: 0, zIndex: 2, display: 'grid', placeItems: 'center', bgcolor: 'rgba(0,0,0,.7)', color: 'white' }}>
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress color="inherit" />
                <Typography fontWeight={700} sx={{ mt: 1.5 }}>Saving photo…</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{
          p: 2,
          pb: 'max(16px, env(safe-area-inset-bottom))',
          gap: 1,
          justifyContent: 'center',
          bgcolor: 'black',
          borderTop: '1px solid rgba(255,255,255,.14)'
        }}>
          <Button
            variant="outlined"
            onClick={handleRecapturePhoto}
            startIcon={<CameraAlt />}
            disabled={syncingImage}
            sx={{ borderRadius: 1.5, color: 'white', borderColor: 'rgba(255,255,255,.6)', minHeight: 48 }}
          >
            {t("Retake")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSaveImage(true)}
            startIcon={syncingImage ? <CircularProgress size={18} color="inherit" /> : <Save />}
            disabled={syncingImage}
            sx={{ borderRadius: 1.5, minHeight: 48, minWidth: 150 }}
          >
            {syncingImage ? 'Saving...' : 'Use photo'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={showSuccessDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, margin: { xs: 1, sm: 2 }, width: { xs: 'calc(100% - 16px)', sm: 'auto' } }
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{
            p: 4,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <Avatar sx={{
              bgcolor: alpha(theme.palette.success.main, 0.2),
              color: theme.palette.success.main,
              width: 80,
              height: 80,
              mb: 2
            }}>
              <CheckCircle sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h5" color="success.main" gutterBottom>
              Inspection Submitted Successfully!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: 400 }}>
              Thank you for completing the vehicle inspection. Our team will review your submission shortly.
            </Typography>
            <Paper sx={{
              p: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              borderRadius: 2,
              mb: 3,
              width: '100%',
              maxWidth: 300,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
            }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Your Reference Number:
              </Typography>
              <Typography variant="h6" color="primary" fontWeight="bold">
                {referenceId}
              </Typography>
            </Paper>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Snackbar for notifications - positioned better for mobile */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: { xs: 'max(12px, env(safe-area-inset-top))', sm: 20 } }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Main Content */}
      <Box>
        {/* Location Bar */}
        <Paper sx={{
          px: 1.25,
          py: 0.75,
          mb: 1.5,
          borderRadius: 1,
          border: 1,
          borderColor: hasCoordinates(location) ? 'divider' : 'warning.main',
          bgcolor: hasCoordinates(location) ? 'info.light' : 'warning.light',
          color: 'text.primary'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOn color={hasCoordinates(location) ? 'primary' : 'warning'} fontSize="small" />
            <Typography variant="caption" fontWeight={600} sx={{ flex: 1 }}>
              {location.locationName}
            </Typography>
            {!hasCoordinates(location) && (
              <Button
                size="small"
                variant="contained"
                color="warning"
                disabled={locationStatus === 'requesting'}
                onClick={() => requestLocation().catch(() => {})}
                sx={{ flexShrink: 0 }}
              >
                {locationStatus === 'requesting' ? 'Checking...' : 'Allow location'}
              </Button>
            )}
          </Box>
        </Paper>

        {/* Customer case details */}
        <Box sx={{ mb: 1.5 }}>
          {detailSections.map(section => (
            <Accordion
              key={section.title}
              disableGutters
              elevation={0}
              sx={{
                mb: 1,
                border: 1,
                borderColor: 'divider',
                borderRadius: '8px !important',
                bgcolor: 'background.paper',
                '&:before': { display: 'none' },
                overflow: 'hidden',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  minHeight: 48,
                  px: 1.5,
                  '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 },
                }}
              >
                <Box sx={{ width: 28, height: 28, display: 'grid', placeItems: 'center', borderRadius: 1, bgcolor: 'info.light', color: 'primary.main' }}>
                  {section.icon}
                </Box>
                <Typography variant="h6">{section.title}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0, borderTop: 1, borderColor: 'divider' }}>
                <KeyValueGrid items={section.items} />
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Required photos */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 1.5, borderRadius: 1, border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 32, height: 32, display: 'grid', placeItems: 'center', borderRadius: 1, bgcolor: 'info.light', color: 'primary.main' }}>
                <CameraAlt fontSize="small" />
              </Box>
              <Typography variant="h5">{t('inspection.sections.RequiredPhotos')}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              {completedPhotos} / {totalPhotos}
            </Typography>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
            {RequiredPhotos.map(photo => {
              const captured = capturedImages[photo.id];
              return (
                <Paper
                  key={photo.id}
                  variant="outlined"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, minHeight: 76, borderColor: captured ? 'success.main' : 'divider' }}
                >
                  <Box
                    component="img"
                    src={captured || carIcons[photo.id]}
                    alt=""
                    sx={{ width: 60, height: 54, flexShrink: 0, objectFit: captured ? 'cover' : 'contain', borderRadius: 1, bgcolor: 'background.default', p: captured ? 0 : 0.5 }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body1" fontWeight={600}>{photo.label}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.35, color: captured ? 'success.main' : 'text.secondary' }}>
                      {captured ? <CheckCircle fontSize="small" /> : <AccessTime fontSize="small" />}
                      <Typography variant="caption" fontWeight={600}>
                        {captured ? 'Captured' : t('inspection.status.pending')}
                      </Typography>
                    </Box>
                  </Box>
                  {captured ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Tooltip title="Retake photo">
                        <IconButton onClick={() => handleOpenCamera(photo.id)} size="small" aria-label={`Retake ${photo.label}`}>
                          <CameraAlt fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete photo">
                        <IconButton onClick={() => handleDeletePhoto(photo.id)} size="small" aria-label={`Delete ${photo.label}`}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Tooltip title={`Capture ${photo.label}`}>
                    <IconButton
                      onClick={() => handleOpenCamera(photo.id)}
                      aria-label={`Capture ${photo.label}`}
                      sx={{
                        flexShrink: 0,
                        width: 40,
                        height: 40,
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': { bgcolor: 'primary.dark' }
                      }}
                    >
                      <CameraAlt fontSize="small" />
                    </IconButton>
                    </Tooltip>
                  )}
                </Paper>
              );
            })}
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              disabled={completedPhotos < totalPhotos || submitting}
              onClick={handleSubmitInspection}
                sx={{
                borderRadius: 0.5,
                px: { xs: 3, sm: 6 },
                width: { xs: '100%', sm: 'auto' },
                minWidth: { sm: 220 },
                fontWeight: 600,
                textTransform: 'none'
              }}
              startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : (completedPhotos === totalPhotos ? <CheckCircle /> : null)}
            >
              {submitting ? t('inspection.buttons.submitting') : t('inspection.buttons.submit')}
            </Button>
        </Box>
      </Box>
      {/* No reference image dialog needed anymore as we show icons directly */}
    </Box>
  );
};

export default InspectionPage;
